import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, DatePicker, Input, Select, Table, Typography } from "antd";
import { searchEntries } from "../../redux/actions";
import CleanStatus from "../CarpetPreview/CleanStatus";
import dateColumn from "../CarpetPreview/dateColumn";
import ShopStatus from "../CarpetPreview/ShopStatus";
import ClientSelect from "./ClientSelect";
import { clientName, dimensionsText, money } from "./format";

const { Title } = Typography;

export default function Entries() {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [status, setStatus] = useState("in");
  const [ref, setRef] = useState("");
  const [clientId, setClientId] = useState(undefined);
  const [box, setBox] = useState("");
  const [dates, setDates] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});

  const load = (nextPage = 1, nextSize = pageSize) => {
    const [from, to] = dates || [];
    setLoading(true);
    dispatch(searchEntries(nextPage, nextSize, {
      status,
      ref,
      client: clientId,
      storageBox: box,
      from: from && from.toISOString(),
      to: to && to.toISOString(),
    }))
      .then(({ payload }) => {
        setRows(payload.data || []);
        setTotal(payload.total || 0);
        setPage(nextPage);
        setPageSize(nextSize);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const handle = setTimeout(() => load(1, pageSize), 300);
    return () => clearTimeout(handle);
  }, [status, ref, clientId, box, dates]);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "entries" })}</Title>
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Select value={status} style={{ width: 140 }} onChange={setStatus}>
          <Select.Option value="in">{intl.formatMessage({ id: "inShop" })}</Select.Option>
          <Select.Option value="out">{intl.formatMessage({ id: "checkedOut" })}</Select.Option>
          <Select.Option value="all">{intl.formatMessage({ id: "all" })}</Select.Option>
        </Select>
        <Input placeholder={intl.formatMessage({ id: "ref" })} value={ref} style={{ width: 100 }} onChange={(event) => setRef(event.target.value)} />
        <ClientSelect value={clientId} onChange={setClientId} style={{ width: 220 }} />
        <Input placeholder={intl.formatMessage({ id: "storageBox" })} value={box} style={{ width: 120 }} onChange={(event) => setBox(event.target.value)} />
        <DatePicker.RangePicker value={dates} onChange={setDates} />
        <Button
          type="primary"
          style={{ marginLeft: "auto" }}
          disabled={!Object.keys(selectedMap).length}
          onClick={() => history.push({
            pathname: "/carpets/report",
            state: { entries: Object.values(selectedMap), sheet: "entries" },
          })}
        >
          {intl.formatMessage({ id: "generateReport" })}
        </Button>
      </div>
      <Table
        size="small"
        bordered
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        pagination={{ current: page, pageSize, total, showSizeChanger: true }}
        onChange={(pagination) => load(pagination.current, pagination.pageSize)}
        rowSelection={{
          selectedRowKeys: Object.keys(selectedMap),
          preserveSelectedRowKeys: true,
          onChange: (keys) => {
            setSelectedMap((current) => {
              const pageRows = new Map(rows.map((row) => [String(row._id), row]));
              const next = {};
              keys.forEach((key) => {
                const record = pageRows.get(String(key)) || current[key] || current[String(key)];
                if (record) next[String(key)] = record;
              });
              return next;
            });
          },
        }}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), width: 70, render: (row) => row.carpet._ref },
          { title: intl.formatMessage({ id: "client" }), render: (row) => clientName(row.client) },
          { title: intl.formatMessage({ id: "category" }), render: (row) => intl.formatMessage({ id: row.carpet.category }) },
          {
            title: intl.formatMessage({ id: "price" }),
            width: 150,
            onHeaderCell: () => ({ style: { textAlign: "center" } }),
            render: (row) => (
              <div style={{ display: "flex", margin: "-8px" }}>
                <div style={{ flex: 1, padding: "8px", borderRight: "1px solid #f0f0f0", textDecoration: row.jobPrice != null && row.jobPrice !== "" ? "line-through" : undefined }}>{money(row.carpet.price)}</div>
                <div style={{ flex: 1, padding: "8px" }}>{money(row.jobPrice)}</div>
              </div>
            ),
          },
          { title: intl.formatMessage({ id: "widthXLength" }), render: (row) => dimensionsText(row.carpet) },
          { title: intl.formatMessage({ id: "storageBox" }), dataIndex: "storageBox", width: 90 },
          { title: intl.formatMessage({ id: "status" }), width: 90, render: (row) => <ShopStatus inShop={!row.outAt} /> },
          { title: intl.formatMessage({ id: "cleanStatus" }), width: 110, render: (row) => <CleanStatus cleaned={row.cleaned} /> },
          dateColumn(intl.formatMessage({ id: "inAt" }), (row) => moment(row.inAt).format("DD/MM/YYYY HH:mm")),
          dateColumn(intl.formatMessage({ id: "outAt" }), (row) => (row.outAt ? moment(row.outAt).format("DD/MM/YYYY HH:mm") : "")),
        ]}
      />
    </div>
  );
}
